import React, { memo, useEffect, useState } from 'react';
import { Badge, Box, Chip, ClickAwayListener, Popper, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import '@fontsource/rubik';
import MarkUnreadChatAltRoundedIcon from '@mui/icons-material/MarkUnreadChatAltRounded';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import BoltIcon from '@mui/icons-material/Bolt';
import StayCurrentLandscapeIcon from '@mui/icons-material/StayCurrentLandscape';
import StayCurrentPortraitIcon from '@mui/icons-material/StayCurrentPortrait';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { getContrastRatio } from '@mui/material/styles';
import { convertTimeSecondsToMinutes, getInitials, timeStampToDayString, timeStampToFormatTimeZone } from '../../../utils/commonServices';
import {
  formatDueDate,
  isOverdue,
  parseDueDate,
  computeCountdown,
  toEpochSeconds,
} from '../../../utils/dueDateUtils';
import DateTimePicker from '../../../components/date-time/DateTimePicker';
import useCustomTheme from '../../../theme/useCustomTheme';
import { KANBAN_STAGES } from '../../../constants/constants';
import CustomTooltip from '../../../components/CustomTooltip';
import { updateDueDateApi } from '../../../services/api';
import { setSnackbar } from '../../../redux/messageSlice';

const KanbanCard = ({ project, stageColor: stageColorProp, stageLabel: stageLabelProp, onClick }) => {
  const commonReducer = useSelector((state) => state.root.commonReducer);
  const dropdownReducer = useSelector((state) => state.root.dropdownReducer);
  const [theme] = useCustomTheme();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const isRestrictedRole =
    commonReducer?.role_name === 'QC' || commonReducer?.role_name === 'Editor';
  const isEditingNotStarted = isRestrictedRole && project?.status_type_order < 1;
  // Editors may now open projects in any stage; Production-stage projects show an
  // "editing not started" view on the detail page rather than being blocked here.
  const isEditorBlocked = false;
  const canShowEditor =
    commonReducer?.role_name === 'Admin' || commonReducer?.role_name === 'QC';
  const isAdmin = commonReducer?.role_name === 'Admin';

  const stage = KANBAN_STAGES.find((s) => s.id === project?.status_type_order);
  const stageKey = stage?.key ?? '';
  const stageLabel = stageLabelProp ?? stage?.label ?? '';
  const stageColor = stageColorProp ?? stage?.color ?? theme.palette.primary.main;

  const dueDate = project?.due_date;
  const dueDateStr = formatDueDate(dueDate);
  const overdue = isOverdue(dueDate);

  const canUpload = project?.flag?.can_upload;
  const hasInternalRevision = project?.flag?.internal_revision && !project?.flag?.internal_revision_rejected && !project?.flag?.can_upload;
  const hasChat = !!project?.flag?.chat;
  const hasClientAction = !!(project?.flag?.editing_cycle_completed && !project?.flag?.new_editing_cycle);
  const hasNewEditingCycle = !!project?.flag?.new_editing_cycle;
  const isQuickTurn = !!project?.quick_turn;
  const isQuickTurnAccepted = isQuickTurn && project?.quick_turn_status === 'accepted';
  const quickTurnTooltip = !isQuickTurn
    ? 'Not a quick turn'
    : project?.quick_turn_status === 'accepted'
      ? 'Quick Turn accepted'
      : project?.quick_turn_status === 'rejected'
        ? `Quick Turn rejected${project?.quick_turn_rejection_note ? `: ${project.quick_turn_rejection_note}` : ''}`
        : project?.quick_turn_status === 'pending'
          ? 'Quick Turn — pending review'
          : 'Not a quick turn';

  // created_by may be missing entirely, or present with only one of name/email populated.
  const createdByName = typeof project?.created_by?.name === 'string' ? project.created_by.name.trim() : '';
  const createdByEmail = typeof project?.created_by?.email === 'string' ? project.created_by.email.trim() : '';
  const hasCreatedBy = !!(createdByName || createdByEmail);
  const createdByTooltip = [createdByName, createdByEmail].filter(Boolean).join(' • ');

  const eventTypeName = (() => {
    if (project?.event_type?.name) return project.event_type.name;
    if (typeof project?.event_type === 'string' && project.event_type) return project.event_type;
    if (project?.event_type_id) {
      return dropdownReducer?.event_type?.find((t) => t.id === project.event_type_id)?.value ?? null;
    }
    return null;
  })();

  const isClientReviewBack = hasNewEditingCycle && (stageKey === 'editing' || stageKey === 'internal_revision');

  const hasClientRevisionTag = Array.isArray(project?.tags) && project.tags.includes('Client Revision');
  const hasInternalRevisionTag = Array.isArray(project?.tags) && project.tags.includes('Internal Revision');

  const avatarColors = [theme.palette.primary.main, '#000000', '#6C00FF'];

  // ─── Countdown timer ────────────────────────────────────────────────────────
  const [countdown, setCountdown] = useState(() => computeCountdown(dueDate));

  useEffect(() => {
    const update = () => setCountdown(computeCountdown(dueDate));
    update();
    if (!dueDate) return;
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [dueDate]);

  // ─── Due-date editor (Admin only) ────────────────────────────────────────
  const [anchorEl, setAnchorEl] = useState(null);
  const [dueDatePickerDate, setDueDatePickerDate] = useState(null);
  const [saving, setSaving] = useState(false);

  const openPopover = (e) => {
    e.stopPropagation();
    setDueDatePickerDate(dueDate ? parseDueDate(dueDate) : new Date());
    setAnchorEl(e.currentTarget);
  };
  const closePopover = () => setAnchorEl(null);

  const saveDueDate = async () => {
    if (!dueDatePickerDate) return;
    setSaving(true);
    const epoch = toEpochSeconds(dueDatePickerDate);
    try {
      await updateDueDateApi({ event_id: project?.id, due_date: epoch });
      dispatch(setSnackbar({ flag: true, type: 'success', message: 'Due date updated successfully' }));
      queryClient.invalidateQueries(['kanban-board-summary']);
      queryClient.invalidateQueries(['kanban-column']);
      closePopover();
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ??
        (status === 422
          ? 'Due date cannot be in the past'
          : status === 403
          ? 'You do not have permission to set due dates'
          : 'Failed to update due date');
      dispatch(setSnackbar({ flag: true, type: 'error', message: msg }));
    } finally {
      setSaving(false);
    }
  };

  const countdownColor = countdown?.color === 'red' ? '#e53935' : countdown?.color === 'yellow' ? '#f9a825' : '#43a047';
  const cardShadow = !countdown
    ? '0 1px 2px rgba(0,0,0,0.06)'
    : countdown.color === 'red'
      ? '0 0 10px 3px rgba(229,57,53,0.18)'
      : countdown.color === 'yellow'
        ? '0 0 10px 3px rgba(249,168,37,0.18)'
        : '0 0 10px 3px rgba(67,160,71,0.18)';

  return (
    <>
    <CustomTooltip
      title={isEditingNotStarted ? 'Editing not started yet' : isEditorBlocked ? 'In production — not available for editing' : null}
      PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] }}
    >
      <Box
        data-testid={`kanban-card-${project?.id}`}
        onClick={(isEditingNotStarted || isEditorBlocked) ? undefined : onClick}
        sx={{
          backgroundColor: isEditingNotStarted ? '#FAFBFC' : isQuickTurnAccepted ? '#fff7d9' : '#FFFFFF',
          borderRadius: '10px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          pt: { xs: '10px', lg: '12px' },
          px: { xs: '12px', lg: '14px' },
          pb: { xs: '6px', lg: '7px' },
          cursor: isEditingNotStarted ? 'default' : isEditorBlocked ? 'not-allowed' : 'pointer',
          transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
          border: `1px solid ${isEditingNotStarted ? '#ECEDF2' : '#E8EAF0'}`,
          borderLeftWidth: '3px',
          borderLeftColor: isEditingNotStarted ? '#D0D3DB' : (countdown ? countdownColor : stageColor),
          '&:hover': isEditingNotStarted
            ? {}
            : {
                boxShadow: cardShadow,
                borderColor: `${countdown ? countdownColor : stageColor}50`,
              },
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          width: '100%',
          height: { xs: '189px', lg: '204px', xl: '254px' },
          flexShrink: 0,
          overflow: 'hidden',
        }}
        aria-label={`Project: ${project?.name}`}
        role="button"
        tabIndex={isEditingNotStarted ? -1 : 0}
        onKeyDown={(e) => {
          if (!isEditingNotStarted && (e.key === 'Enter' || e.key === ' ')) onClick?.();
        }}
      >
        {/* Top row — all status icons (active: coloured, inactive: dimmed) + client-review badge */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '18px' }}>
          {/* Revision badges — flag-based + tag-based */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
            {isClientReviewBack && (
              <Chip
                icon={<ReplayRoundedIcon sx={{ fontSize: '10px !important', color: '#7C3AED !important' }} />}
                label="Client Rev."
                size="small"
                sx={{
                  height: '16px',
                  fontFamily: 'Rubik',
                  fontSize: { xs: '8px', lg: '9px' },
                  fontWeight: 600,
                  color: '#7C3AED',
                  backgroundColor: '#EDE9FE',
                  border: '1px solid #C4B5FD',
                  borderRadius: '4px',
                  '& .MuiChip-label': { px: '5px' },
                  '& .MuiChip-icon': { ml: '4px' },
                }}
              />
            )}
            {hasClientRevisionTag && (
              <CustomTooltip title="Returned from Client Revision stage">
                <Chip
                  icon={<ReplayRoundedIcon sx={{ fontSize: '10px !important', color: '#B45309 !important' }} />}
                  label="Client Rev."
                  size="small"
                  sx={{
                    height: '16px',
                    fontFamily: 'Rubik',
                    fontSize: { xs: '8px', lg: '9px' },
                    fontWeight: 600,
                    color: '#B45309',
                    backgroundColor: '#FEF3C7',
                    border: '1px solid #FCD34D',
                    borderRadius: '4px',
                    '& .MuiChip-label': { px: '5px' },
                    '& .MuiChip-icon': { ml: '4px' },
                  }}
                />
              </CustomTooltip>
            )}
            {hasInternalRevisionTag && (
              <CustomTooltip title="Returned from Internal Revision stage">
                <Chip
                  icon={<VisibilityOutlinedIcon sx={{ fontSize: '10px !important', color: '#1565C0 !important' }} />}
                  label="Internal Rev."
                  size="small"
                  sx={{
                    height: '16px',
                    fontFamily: 'Rubik',
                    fontSize: { xs: '8px', lg: '9px' },
                    fontWeight: 600,
                    color: '#1565C0',
                    backgroundColor: '#E3F2FD',
                    border: '1px solid #90CAF9',
                    borderRadius: '4px',
                    '& .MuiChip-label': { px: '5px' },
                    '& .MuiChip-icon': { ml: '4px' },
                  }}
                />
              </CustomTooltip>
            )}
          </Box>

          {/* Status icons — all rendered, inactive ones dimmed */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CustomTooltip title={quickTurnTooltip}>
              <BoltIcon sx={{ fontSize: { xs: '12px', lg: '14px' }, color: isQuickTurnAccepted ? '#f59e0b' : '#D0D3DB', opacity: isQuickTurnAccepted ? 1 : 0.5 }} />
            </CustomTooltip>
            <CustomTooltip title={hasChat ? 'Revisions logged, not submitted' : 'No pending revisions'}>
              <MarkUnreadChatAltRoundedIcon sx={{ fontSize: { xs: '12px', lg: '14px' }, color: hasChat ? '#E4D00A' : '#D0D3DB', opacity: hasChat ? 1 : 0.5 }} />
            </CustomTooltip>
            <CustomTooltip title={hasInternalRevision ? 'Internal review pending' : 'No internal review'}>
              <VisibilityOutlinedIcon sx={{ fontSize: { xs: '12px', lg: '14px' }, color: hasInternalRevision ? '#1565C0' : '#D0D3DB', opacity: hasInternalRevision ? 1 : 0.5 }} />
            </CustomTooltip>
            <CustomTooltip title={hasClientAction ? 'Action required from client' : 'No client action needed'}>
              <FiberManualRecordIcon sx={{ fontSize: { xs: '12px', lg: '14px' }, color: hasClientAction ? '#E53935' : '#D0D3DB', opacity: hasClientAction ? 1 : 0.5 }} />
            </CustomTooltip>
            <CustomTooltip title={canUpload ? 'Upload ready' : 'Upload not ready'}>
              <CloudUploadOutlinedIcon sx={{ fontSize: { xs: '12px', lg: '14px' }, color: canUpload ? theme.palette.primary.main : '#D0D3DB', opacity: canUpload ? 1 : 0.5 }} />
            </CustomTooltip>
            <CustomTooltip title={project?.editing_cycle_count ? `Revision cycle ${project.editing_cycle_count}` : 'No revision cycles'}>
              <Badge
                badgeContent={project?.editing_cycle_count || null}
                color="secondary"
                sx={{ '& .MuiBadge-badge': { padding: '0 3px', fontSize: '8px', minWidth: '14px', height: '14px' } }}
              >
                <MovieOutlinedIcon sx={{ fontSize: { xs: '12px', lg: '14px' }, color: project?.editing_cycle_count ? '#555' : '#D0D3DB', opacity: project?.editing_cycle_count ? 1 : 0.5 }} />
              </Badge>
            </CustomTooltip>
            {isEditingNotStarted && (
              <NotInterestedIcon sx={{ color: theme.palette.error.main, fontSize: { xs: '12px', lg: '14px' } }} />
            )}
          </Box>
        </Box>

        {/* Project name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, flexShrink: 0 }}>
          <CustomTooltip title={project?.video_orientation === 'portrait' ? 'Portrait' : project?.video_orientation === 'square' ? 'Square' : 'Landscape'}>
            {project?.video_orientation === 'portrait' ? (
              <StayCurrentPortraitIcon sx={{ fontSize: { xs: '11px', sm: '12px', lg: '13px', xl: '15px' }, color: '#777', flexShrink: 0 }} />
            ) : project?.video_orientation === 'square' ? (
              <FitScreenIcon sx={{ fontSize: { xs: '11px', sm: '12px', lg: '13px', xl: '15px' }, color: '#777', flexShrink: 0 }} />
            ) : (
              <StayCurrentLandscapeIcon sx={{ fontSize: { xs: '11px', sm: '12px', lg: '13px', xl: '15px' }, color: '#777', flexShrink: 0 }} />
            )}
          </CustomTooltip>
          <Typography
            sx={{
              fontSize: { xs: '11px', sm: '12px', lg: '13px', xl: '15px' },
              lineHeight: 1.4,
              fontWeight: 700,
              fontFamily: 'Rubik',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: isEditingNotStarted ? '#999' : '#111',
              flex: 1,
              minWidth: 0,
            }}
            title={project?.name}
          >
            {project?.name}
          </Typography>
          {project?.lucid_link_url && !isEditingNotStarted && !isEditorBlocked && (
            <CustomTooltip title="Open in Lucid">
              <Box
                component="a"
                href={project.lucid_link_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{ display: 'flex', alignItems: 'center', color: '#5A6474', textDecoration: 'none', flexShrink: 0 }}
              >
                <OpenInNewIcon sx={{ fontSize: { xs: '10px', lg: '12px' } }} />
              </Box>
            </CustomTooltip>
          )}
        </Box>

        {/* Event type */}
        {eventTypeName && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start', backgroundColor: '#EAECF5', borderRadius: '4px', px: '7px', py: '2px' }}>
            <Typography sx={{ fontSize: { xs: '9px', lg: '10px', xl: '12px' }, fontFamily: 'Rubik', fontWeight: 600, color: '#4A4F6A', whiteSpace: 'nowrap' }}>
              {eventTypeName}
            </Typography>
          </Box>
        )}

        {/* Organisation name */}
        {project?.organisation_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '16px' }}>
            <BusinessOutlinedIcon sx={{ fontSize: { xs: '10px', lg: '12px' }, color: '#999' }} />
            <Typography
              sx={{
                fontSize: { xs: '9px', lg: '10px' },
                fontFamily: 'Rubik',
                color: '#888',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {project.organisation_name}
            </Typography>
          </Box>
        )}

        {/* Created by */}
        {hasCreatedBy && (
          <CustomTooltip title={createdByTooltip}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '16px' }}>
              <PersonOutlineIcon sx={{ fontSize: { xs: '10px', lg: '12px' }, color: '#999', flexShrink: 0 }} />
              {createdByName && (
                <Typography
                  sx={{
                    fontSize: { xs: '9px', lg: '10px' },
                    fontFamily: 'Rubik',
                    fontWeight: 600,
                    color: '#888',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexShrink: 0,
                    maxWidth: '55%',
                  }}
                >
                  {createdByName}
                </Typography>
              )}
              {createdByEmail && (
                <Typography
                  sx={{
                    fontSize: { xs: '9px', lg: '10px' },
                    fontFamily: 'Rubik',
                    color: '#aaa',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {createdByName ? `• ${createdByEmail}` : createdByEmail}
                </Typography>
              )}
            </Box>
          </CustomTooltip>
        )}

        {/* Duration — visible to all roles */}
        {project?.video_length && (
          <Typography sx={{ fontSize: { xs: '9px', lg: '10px' }, fontFamily: 'Rubik', fontWeight: 500, color: '#888', whiteSpace: 'nowrap' }}>
            Duration: {convertTimeSecondsToMinutes(project.video_length)}
          </Typography>
        )}

        {/* Assigned editors — Admin and QC only */}
        {canShowEditor && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', minHeight: '18px', flexWrap: 'wrap' }}>
            <PeopleOutlineIcon
              sx={{ fontSize: { xs: '11px', lg: '13px' }, color: '#777', flexShrink: 0 }}
            />
            {project?.editors?.length > 0 ? (
              <>
                {project.editors.slice(0, 3).map((editor, idx) => {
                  const editorName = typeof editor === 'string' ? editor : editor?.name;
                  return editorName ? (
                    <CustomTooltip key={idx} title={editorName}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          height: { xs: '14px', lg: '16px' },
                          width: { xs: '14px', lg: '16px' },
                          fontSize: { xs: '7px', lg: '8px' },
                          fontFamily: 'Rubik',
                          fontWeight: 600,
                          backgroundColor: avatarColors[idx] ?? '#999',
                          color:
                            getContrastRatio(avatarColors[idx] ?? '#999', '#ffffff') > 2.5
                              ? '#ffffff'
                              : '#000000',
                        }}
                      >
                        {getInitials(editorName)}
                      </Box>
                    </CustomTooltip>
                  ) : null;
                })}
                {project.editors.some((e) => typeof e === 'object' && e?.auto_assigned) && (
                  <CustomTooltip title="Auto-assigned by system">
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '2px',
                        backgroundColor: '#E8F5E9',
                        border: '1px solid #A5D6A7',
                        borderRadius: '4px',
                        px: '4px',
                        height: { xs: '14px', lg: '16px' },
                      }}
                    >
                      <AutoFixHighRoundedIcon sx={{ fontSize: { xs: '8px', lg: '9px' }, color: '#2E7D32' }} />
                      <Typography sx={{ fontSize: { xs: '7px', lg: '8px' }, fontFamily: 'Rubik', fontWeight: 600, color: '#2E7D32', lineHeight: 1 }}>
                        Auto
                      </Typography>
                    </Box>
                  </CustomTooltip>
                )}
              </>
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: '9px', lg: '10px' },
                  fontFamily: 'Rubik',
                  color: '#aaa',
                  fontStyle: 'italic',
                }}
              >
                Unassigned
              </Typography>
            )}
          </Box>
        )}

        {/* Editing start date row */}
        {project?.editor_start_date && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <EditCalendarOutlinedIcon sx={{ fontSize: { xs: '11px', lg: '13px' }, color: '#888' }} />
            <Typography sx={{ fontSize: { xs: '9px', lg: '10px', xl: '11px' }, fontFamily: 'Rubik', fontWeight: 500, color: '#555' }}>
              Edit Start
              <Typography component='span' sx={{ fontSize: { xs: '9px', lg: '10px', xl: '11px' }, fontFamily: 'Rubik', fontWeight: 700, ml: '4px', color: '#444' }}>
                {timeStampToDayString(project.editor_start_date)}, {timeStampToFormatTimeZone(project.editor_start_date)}
              </Typography>
            </Typography>
          </Box>
        )}

        {/* Due date row */}
        <Box
          onClick={isAdmin ? openPopover : undefined}
          sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: isAdmin ? 'pointer' : 'default' }}
        >
          {overdue ? (
            <EventBusyOutlinedIcon sx={{ fontSize: { xs: '11px', lg: '13px' }, color: '#e53935' }} />
          ) : (
            <EventAvailableOutlinedIcon sx={{ fontSize: { xs: '11px', lg: '13px' }, color: countdown ? countdownColor : (dueDateStr ? '#555' : '#bbb') }} />
          )}
          {dueDateStr ? (
            <Typography sx={{ fontSize: { xs: '9px', lg: '10px', xl: '11px' }, fontFamily: 'Rubik', fontWeight: 500, color: overdue ? '#e53935' : countdown ? countdownColor : '#555' }}>
              Due Date
              <Typography component='span' sx={{ fontSize: { xs: '9px', lg: '10px', xl: '11px' }, fontFamily: 'Rubik', fontWeight: 700, ml: '4px', color: overdue ? '#e53935' : countdown ? countdownColor : '#444' }}>
                {dueDateStr}
              </Typography>
            </Typography>
          ) : (
            <Typography sx={{ fontSize: { xs: '9px', lg: '10px' }, fontFamily: 'Rubik', color: '#bbb', fontStyle: 'italic' }}>
              {isAdmin ? 'Set due date' : 'No due date'}
            </Typography>
          )}
          {isAdmin && (
            <CustomTooltip title={dueDateStr ? 'Edit due date' : 'Set due date'}>
              <EditCalendarOutlinedIcon sx={{ fontSize: { xs: '11px', lg: '13px' }, color: '#bbb', ml: 'auto' }} />
            </CustomTooltip>
          )}
        </Box>

        {/* Countdown timer — shown when due date is within 24 hours */}
        {countdown && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <TimerOutlinedIcon sx={{ fontSize: { xs: '10px', lg: '12px' }, color: countdownColor }} />
            <Typography
              sx={{
                fontSize: { xs: '9px', lg: '10px' },
                fontFamily: 'Rubik',
                fontWeight: 700,
                color: countdownColor,
                letterSpacing: '0.03em',
              }}
            >
              {countdown.overdue
                ? 'Overdue!'
                : countdown.d > 0
                  ? `${countdown.d}d ${String(countdown.h).padStart(2,'0')}:${String(countdown.m).padStart(2,'0')}:${String(countdown.s).padStart(2,'0')}`
                  : `${String(countdown.h).padStart(2,'0')}:${String(countdown.m).padStart(2,'0')}:${String(countdown.s).padStart(2,'0')}`}
            </Typography>
          </Box>
        )}
      </Box>
    </CustomTooltip>

    {/* Due-date custom picker — Admin only */}
    {isAdmin && Boolean(anchorEl) && (
      <Popper open anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={(e) => { e.stopPropagation?.(); closePopover(); }}>
          <Box onClick={(e) => e.stopPropagation()}>
            <DateTimePicker
              extendEditingDate={true}
              placeholder="Select due date & time"
              defaultValue={dueDatePickerDate || new Date()}
              dateTime={dueDatePickerDate || new Date()}
              setDateTime={setDueDatePickerDate}
              onDateChange={(seconds) => setDueDatePickerDate(new Date(seconds * 1000))}
              buttonClickHandler={saveDueDate}
              buttonDisabled={saving}
              medium={true}
            />
          </Box>
        </ClickAwayListener>
      </Popper>
    )}
  </>
  );
};

export default memo(KanbanCard);
